import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAccount } from "../../context/AccountContext";

const fmt   = (n, d = 2) => n == null ? "—" : Number(n).toFixed(d);
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const fmtExpMonth = (d) => {
  if (!d) return "—";
  const parts = d.split("-");
  if (parts.length < 2) return "—";
  const mon = MONTHS[parseInt(parts[1], 10) - 1] || "";
  return `${mon} ${parts[0].slice(2)}`;
};
const fmtN  = (n) => n == null ? "—" : new Intl.NumberFormat("en-US").format(n);
const pnlColor = (v) => v == null ? "#333" : v >= 0 ? "#28a745" : "#dc3545";
const pnlFmt = (v) => {
  if (v == null) return "—";
  const sign = v >= 0 ? "+" : "-";
  return `${sign}$${new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(Math.abs(v))}`;
};

// Signed strike-distance: positive = OTM, negative = ITM
function calcStrikeDistance(row) {
  const ltp    = row.underlying_ltp;
  const strike = row.strikeprice;
  if (!ltp || !strike || ltp <= 0) return null;
  if (row.optiontype === "CE") return (strike - ltp) / ltp * 100;
  if (row.optiontype === "PE") return (ltp - strike) / ltp * 100;
  return null;
}

// For a list of rows, returns the minimum (most-threatened) signed distance
function nearestDist(rows) {
  const vals = rows.map(calcStrikeDistance).filter(v => v !== null);
  return vals.length ? Math.min(...vals) : null;
}

// Compute per-symbol nearest distances using only active (non-zero lot) positions
function computeSymbolDistances(allRows) {
  const bySym = {};
  allRows.forEach(r => {
    if (!r.totallots) return;   // skip closed positions
    const sym = r.symbolname;
    if (!bySym[sym]) bySym[sym] = { CE: [], PE: [] };
    if (r.optiontype === "CE" || r.optiontype === "PE") bySym[sym][r.optiontype].push(r);
  });
  const result = {};
  Object.entries(bySym).forEach(([sym, sides]) => {
    result[sym] = {
      nearestCall: nearestDist(sides.CE),
      nearestPut:  nearestDist(sides.PE),
    };
  });
  return result;
}

const fmtDist = (v) => {
  if (v == null) return "—";
  const sign = v >= 0 ? "+" : "";
  return `${sign}${v.toFixed(2)}%`;
};
const distColor = (v) => v == null ? "#888" : v < 0 ? "#dc3545" : v < 1.5 ? "#fd7e14" : "#28a745";

export default function OptionPositions() {
  const { selectedAccount } = useAccount();
  const isAllAccounts = selectedAccount === "All Accounts";
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter]   = useState({ symbol: "", type: "", expiry: "", account: "" });
  const [sort, setSort]       = useState({ col: "symbolname", dir: "asc" });

  const cycleSort = (col) =>
    setSort(s => s.col === col ? { col, dir: s.dir === "asc" ? "desc" : "asc" } : { col, dir: "asc" });
  const sortArrow = (col) => sort.col === col ? (sort.dir === "asc" ? " ▲" : " ▼") : " ↕";

  useEffect(() => {
    if (selectedAccount) fetchPositions();
  }, [selectedAccount]);

  const fetchPositions = async () => {
    setLoading(true);
    try {
      const res = await axios.get("/trading/option-positions", {
        params: { username: selectedAccount }
      });
      setRows(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const symDists = computeSymbolDistances(rows);

  const enriched = rows.map(r => ({
    ...r,
    nearest_put_pct:  symDists[r.symbolname]?.nearestPut  ?? null,
    nearest_call_pct: symDists[r.symbolname]?.nearestCall ?? null,
  }));

  const filtered = enriched
    .filter(r => {
      if (!r.netqty) return false;          // hide zero-qty (closed) positions
      const sym = filter.symbol  ? r.symbolname?.toLowerCase().includes(filter.symbol.toLowerCase()) : true;
      const typ = filter.type    ? r.optiontype === filter.type : true;
      const exp = filter.expiry  ? r.expirydate === filter.expiry : true;
      const acc = filter.account ? r.username === filter.account : true;
      return sym && typ && exp && acc;
    })
    .sort((a, b) => {
      const v = sort.dir === "asc" ? 1 : -1;
      if (sort.col === "symbolname")      return v * (a.symbolname || "").localeCompare(b.symbolname || "");
      if (sort.col === "nearest_put_pct" || sort.col === "nearest_call_pct" ||
          sort.col === "netqty" || sort.col === "totallots" || sort.col === "unrealised") {
        const av = a[sort.col] ?? (sort.dir === "asc" ? Infinity : -Infinity);
        const bv = b[sort.col] ?? (sort.dir === "asc" ? Infinity : -Infinity);
        return v * (av - bv);
      }
      return 0;
    });

  const expiries  = [...new Set(rows.map(r => r.expirydate).filter(Boolean))].sort();
  const accounts  = [...new Set(rows.map(r => r.username).filter(Boolean))].sort();

  // Totals
  const totalUnrealised = filtered.reduce((s, r) => s + (r.unrealised || 0), 0);
  const totalRealised   = filtered.reduce((s, r) => s + (r.realised   || 0), 0);
  const totalPotential  = filtered.reduce((s, r) => s + (r.potential_pnl || 0), 0);
  const activeCount     = filtered.filter(r => r.netqty !== 0).length;

  return (
    <div style={{ padding: "20px 25px 30px", background: "#f4f6f9", minHeight: "100vh" }}>

      <div style={{ marginBottom: 20 }}>
        <h2 style={pageTitle}>Option Positions</h2>
        <p style={{ color: "#888", fontSize: 13, margin: 0 }}>
          {isAllAccounts ? "Option positions across all accounts" : `All open and historical option positions for ${selectedAccount}`}
        </p>
      </div>

      {/* Summary strip */}
      <div style={summaryStrip}>
        <SummaryTile label="Active Positions" value={activeCount} color="#007bff" />
        <SummaryTile label="Total Positions" value={filtered.length} color="#6c757d" />
        <SummaryTile label="Unrealised P&L" value={pnlFmt(totalUnrealised)} color={pnlColor(totalUnrealised)} />
        <SummaryTile label="Realised P&L" value={pnlFmt(totalRealised)} color={pnlColor(totalRealised)} />
        <SummaryTile label="Potential P&L" value={pnlFmt(totalPotential)} color="#17a2b8" />
      </div>

      {/* Filters */}
      <div style={filterBar}>
        <input
          placeholder="Filter by symbol…"
          value={filter.symbol}
          onChange={e => setFilter(f => ({ ...f, symbol: e.target.value }))}
          style={filterInput}
        />
        <select value={filter.type} onChange={e => setFilter(f => ({ ...f, type: e.target.value }))} style={filterSelect}>
          <option value="">All Types</option>
          <option value="CE">CE (Call)</option>
          <option value="PE">PE (Put)</option>
        </select>
        <select value={filter.expiry} onChange={e => setFilter(f => ({ ...f, expiry: e.target.value }))} style={filterSelect}>
          <option value="">All Expiries</option>
          {expiries.map(e => <option key={e} value={e}>{e}</option>)}
        </select>
        {isAllAccounts && (
          <select value={filter.account} onChange={e => setFilter(f => ({ ...f, account: e.target.value }))} style={filterSelect}>
            <option value="">All Accounts</option>
            {accounts.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        )}
        <button onClick={fetchPositions} style={refreshBtn}>Refresh</button>
      </div>

      {/* Table */}
      <div style={tableWrap}>
        {loading ? (
          <div style={centered}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div style={centered}>No option positions found.</div>
        ) : (
          <table style={table}>
            <thead>
              <tr>
                {[
                  ...(isAllAccounts ? [{ label: "Account", col: null, align: "left" }] : []),
                  { label: "Symbol",     col: "symbolname", align: "left"  },
                  { label: "Type",       col: null,         align: "left"  },
                  { label: "Strike",     col: null,         align: "right" },
                  { label: "Expiry",     col: null,         align: "left"  },
                  { label: "Exp Month",  col: null,         align: "left"  },
                  { label: "Net Qty",    col: "netqty",     align: "right" },
                  { label: "Lots",       col: "totallots",  align: "right" },
                  { label: "LTP",        col: null,         align: "right" },
                  { label: "Buy Avg",    col: null,         align: "right" },
                  { label: "Sell Avg",   col: null,         align: "right" },
                  { label: "Delta",      col: null,         align: "right" },
                  { label: "IV",         col: null,         align: "right" },
                  { label: "Theta",      col: null,         align: "right" },
                  { label: "Nearest Put %",  col: "nearest_put_pct",  align: "right" },
                  { label: "Nearest Call %", col: "nearest_call_pct", align: "right" },
                  { label: "Unrealised", col: "unrealised",  align: "right" },
                  { label: "Realised",   col: null,         align: "right" },
                  { label: "Potential",  col: null,         align: "right" },
                ].map(({ label, col, align }) => (
                  <th key={label} onClick={col ? () => cycleSort(col) : undefined} style={{
                    ...th, textAlign: align,
                    color: col ? "#007bff" : "#6c757d",
                    cursor: col ? "pointer" : "default", userSelect: "none"
                  }}>
                    {label}{col ? sortArrow(col) : ""}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => {
                const isActive = r.netqty !== 0;
                return (
                  <tr key={i} style={{ background: isActive ? "#fff" : "#fafafa" }}>
                    {isAllAccounts && (
                      <td style={{ ...td, fontWeight: 600, color: "#6f42c1" }}>{r.username}</td>
                    )}
                    <td style={td}><strong>{r.symbolname}</strong></td>
                    <td style={td}>
                      <span style={{ ...typeBadge, background: r.optiontype === "CE" ? "#d4edda" : "#fde2e2", color: r.optiontype === "CE" ? "#155724" : "#721c24" }}>
                        {r.optiontype}
                      </span>
                    </td>
                    <td style={{ ...td, textAlign: "right" }}>{fmtN(r.strikeprice)}</td>
                    <td style={td}>{r.expirydate}</td>
                    <td style={td}>{fmtExpMonth(r.expirydate)}</td>
                    <td style={{ ...td, textAlign: "right", fontWeight: isActive ? 700 : 400, color: isActive ? "#2c3e50" : "#aaa" }}>
                      {r.netqty}
                    </td>
                    <td style={{ ...td, textAlign: "right" }}>{r.totallots}</td>
                    <td style={{ ...td, textAlign: "right" }}>{fmt(r.ltp)}</td>
                    <td style={{ ...td, textAlign: "right" }}>{fmt(r.totalbuyavgprice)}</td>
                    <td style={{ ...td, textAlign: "right" }}>{fmt(r.totalsellavgprice)}</td>
                    <td style={{ ...td, textAlign: "right", color: r.delta < 0 ? "#dc3545" : "#333" }}>{fmt(r.delta, 3)}</td>
                    <td style={{ ...td, textAlign: "right" }}>{fmt(r.implied_volatility, 1)}%</td>
                    <td style={{ ...td, textAlign: "right" }}>{fmt(r.theta, 3)}</td>
                    <td style={{ ...td, textAlign: "right", fontWeight: 600, color: distColor(r.nearest_put_pct) }}>
                      {r.nearest_put_pct != null ? (
                        <span style={r.nearest_put_pct < 0 ? itmBadge : undefined}>
                          {fmtDist(r.nearest_put_pct)}
                        </span>
                      ) : "—"}
                    </td>
                    <td style={{ ...td, textAlign: "right", fontWeight: 600, color: distColor(r.nearest_call_pct) }}>
                      {r.nearest_call_pct != null ? (
                        <span style={r.nearest_call_pct < 0 ? itmBadge : undefined}>
                          {fmtDist(r.nearest_call_pct)}
                        </span>
                      ) : "—"}
                    </td>
                    <td style={{ ...td, textAlign: "right", color: pnlColor(r.unrealised), fontWeight: 600 }}>{pnlFmt(r.unrealised)}</td>
                    <td style={{ ...td, textAlign: "right", color: pnlColor(r.realised) }}>{pnlFmt(r.realised)}</td>
                    <td style={{ ...td, textAlign: "right", color: "#17a2b8" }}>{pnlFmt(r.potential_pnl)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function SummaryTile({ label, value, color }) {
  return (
    <div style={tile}>
      <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 700, color }}>{value}</div>
    </div>
  );
}

/* styles */
const pageTitle = { fontSize: 22, fontWeight: 700, color: "#2c3e50", margin: 0 };
const summaryStrip = { display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 12, marginBottom: 20 };
const tile = { background: "#fff", borderRadius: 10, padding: "14px 16px", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" };
const filterBar = { display: "flex", gap: 10, marginBottom: 16, alignItems: "center", flexWrap: "wrap" };
const filterInput = { padding: "7px 12px", borderRadius: 8, border: "1px solid #ccc", fontSize: 13, minWidth: 180 };
const filterSelect = { padding: "7px 10px", borderRadius: 8, border: "1px solid #ccc", fontSize: 13 };
const refreshBtn = { padding: "7px 14px", borderRadius: 8, border: "none", background: "#007bff", color: "#fff", cursor: "pointer", fontSize: 13, marginLeft: "auto" };
const tableWrap = { background: "#fff", borderRadius: 12, boxShadow: "0 2px 10px rgba(0,0,0,0.06)", overflow: "auto" };
const table = { width: "100%", borderCollapse: "collapse", fontSize: 13 };
const th = { padding: "10px 12px", background: "#f8f9fa", borderBottom: "2px solid #dee2e6", fontWeight: 600, fontSize: 11, color: "#6c757d", textTransform: "uppercase", whiteSpace: "nowrap" };
const td = { padding: "9px 12px", borderBottom: "1px solid #f0f0f0", whiteSpace: "nowrap" };
const centered = { padding: 40, textAlign: "center", color: "#888" };
const typeBadge = { display: "inline-block", padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 700 };
const itmBadge  = { display: "inline-block", padding: "1px 6px", borderRadius: 6, background: "#fde2e2", color: "#dc3545" };
